import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ActionPlanCard = ({ actionPlan }) => {
  const [completedItems, setCompletedItems] = React.useState(new Set());

  const toggleCompletion = (itemId) => {
    const newCompleted = new Set(completedItems);
    if (newCompleted?.has(itemId)) {
      newCompleted?.delete(itemId);
    } else {
      newCompleted?.add(itemId);
    }
    setCompletedItems(newCompleted);
  };

  const getTimelineIcon = (type) => {
    switch (type) {
      case 'course': return 'BookOpen';
      case 'certification': return 'Award';
      case 'project': return 'Code';
      case 'networking': return 'Users';
      default: return 'CheckCircle';
    }
  };

  const getTimelineColor = (priority) => {
    switch (priority) {
      case 'High': return 'border-error bg-error/5';
      case 'Medium': return 'border-warning bg-warning/5';
      default: return 'border-primary bg-primary/5';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="Calendar" size={20} className="text-accent" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Personalized Action Plan</h3>
          <p className="text-sm text-text-secondary">Your roadmap to career success</p>
        </div>
      </div>
      <div className="space-y-6">
        {actionPlan?.phases?.map((phase, phaseIndex) => (
          <div key={phaseIndex} className="relative">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-sm font-medium">
                {phaseIndex + 1}
              </div>
              <div>
                <h4 className="font-semibold text-text-primary">{phase?.title}</h4>
                <p className="text-sm text-text-secondary">{phase?.duration}</p>
              </div>
            </div>

            <div className="ml-11 space-y-3">
              {phase?.items?.map((item, itemIndex) => (
                <div
                  key={itemIndex}
                  className={`border rounded-lg p-4 spring-transition ${getTimelineColor(item?.priority)} ${
                    completedItems?.has(`${phaseIndex}-${itemIndex}`) ? 'opacity-60' : ''
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <button
                      onClick={() => toggleCompletion(`${phaseIndex}-${itemIndex}`)}
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 spring-transition ${
                        completedItems?.has(`${phaseIndex}-${itemIndex}`)
                          ? 'bg-success border-success text-success-foreground'
                          : 'border-border hover:border-primary'
                      }`}
                    >
                      {completedItems?.has(`${phaseIndex}-${itemIndex}`) && (
                        <Icon name="Check" size={12} />
                      )}
                    </button>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon name={getTimelineIcon(item?.type)} size={16} className="text-primary" />
                        <h5 className="font-medium text-text-primary">{item?.title}</h5>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          item?.priority === 'High' ? 'bg-error/10 text-error' :
                          item?.priority === 'Medium'? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary'
                        }`}>
                          {item?.priority}
                        </span>
                      </div>
                      <p className="text-sm text-text-secondary mb-3">{item?.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-xs text-text-secondary">
                          <span>⏱️ {item?.estimatedTime}</span>
                          <span>💰 {item?.cost}</span>
                        </div>
                        {item?.link && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(item?.link, '_blank')}
                            iconName="ExternalLink"
                            iconPosition="right"
                          >
                            Start
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {phaseIndex < actionPlan?.phases?.length - 1 && (
              <div className="ml-4 w-0.5 h-6 bg-border mt-2"></div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="text-sm text-text-secondary">
            <span className="font-medium text-text-primary">{completedItems?.size}</span> of{' '}
            <span className="font-medium text-text-primary">
              {actionPlan?.phases?.reduce((total, phase) => total + phase?.items?.length, 0)}
            </span>{' '}
            tasks completed
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-20 bg-muted rounded-full h-2">
              <div
                className="bg-success h-2 rounded-full spring-transition"
                style={{
                  width: `${(completedItems?.size / actionPlan?.phases?.reduce((total, phase) => total + phase?.items?.length, 0)) * 100}%`
                }}
              ></div>
            </div>
            <span className="text-sm font-medium text-success">
              {Math.round((completedItems?.size / actionPlan?.phases?.reduce((total, phase) => total + phase?.items?.length, 0)) * 100)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionPlanCard;